import {clearInputFormErrorFields, inputFormIsValid} from "../../../Components/Inputs";
import {
    JOB_ROLES,
    ROLES_WITHOUT_SUBTYPES,
} from "../../../SiteConfig/jobTitles";
import {isTrimmedStringEmpty} from "../../../Utilities/commonUtilities";
import {isValidEmail} from "../../../Components/Inputs/inputUtility";
import {
    getLoggedInUser
} from "../../../Utilities/LocalStorage/storageUtilities";
import {allow, policyEvents} from "../../../Components/Authorize";
import type {InputFormField, User} from "../../../types";

type UserDetailsForm = Record<string, InputFormField>;

export function isUserDetailsFormValid(userDetailsForm: UserDetailsForm, setWarnings: (warnings: string[]) => void, enablePasswordFields: boolean, isEditing: boolean, patron: User, districts: { id: string; districtName: string }[]) {
    clearInputFormErrorFields(userDetailsForm);

    if (isEditing) {
        checkAndSetError([
            userDetailsForm.email,
            userDetailsForm.lastName,
            userDetailsForm.firstName,
            userDetailsForm.jobTitle,
        ]);

        if (userDetailsForm.confirmEmail.value === '' && userDetailsForm.email.value !== patron.email)
            userDetailsForm.email.setError("Email must match confirmation field.");
        else if (userDetailsForm.confirmEmail.value !== '' && userDetailsForm.confirmEmail.value !== userDetailsForm.email.value)
            userDetailsForm.email.setError("Email must match confirmation field.");
    } else {
        checkAndSetError([
            userDetailsForm.confirmPassword,
            userDetailsForm.password,
            userDetailsForm.email,
            userDetailsForm.lastName,
            userDetailsForm.firstName,
            userDetailsForm.jobTitle,
        ]);

        if (isTrimmedStringEmpty(userDetailsForm.floridaShippingConfirmed.value))
            userDetailsForm.floridaShippingConfirmed.setError("You must acknowledge the Florida shipping policy to register.");

        if (userDetailsForm.password.value !== userDetailsForm.confirmPassword.value && enablePasswordFields)
            userDetailsForm.password.setError("Password must match confirmation field.");
    }

    if (userDetailsForm.jobTitle.value) {
        const isRoleWithoutSubtypes = ROLES_WITHOUT_SUBTYPES.includes(userDetailsForm.jobTitle.value);

        if (isRoleWithoutSubtypes) {
            // Community Partner, Department of Corrections, State Agency: require district + organization
            checkAndSetError([userDetailsForm.districtId], "District is required");
            checkAndSetError([userDetailsForm.organization]);
        } else if (userDetailsForm.patronSubType.value) {
            // All roles with subtypes require district, except RMTC
            if (userDetailsForm.jobTitle.value !== JOB_ROLES.Rmtc) {
                checkAndSetError([userDetailsForm.districtId], "District is required");
            }
        } else {
            userDetailsForm.patronSubType.setError("Role is required");
        }
    }

    // If the selected district is "Other" require organization
    if (userDetailsForm.districtId.value && isDistrictSelectionOther(String(userDetailsForm.districtId.value), districts)) {
        checkAndSetError([userDetailsForm.organization]);
    }

    let isValid = inputFormIsValid(userDetailsForm);

    if (userDetailsForm.password.error === "Password must match confirmation field.") {
        setWarnings([
            "The password fields do not match. Please review your responses and try submitting again.",
        ]);
    } else if (!isValid)
        setWarnings([
            "Missing required items.  Please review your responses and try submitting again.",
        ]);

    return isValid;
}


export function getWarningMessageForUserDetailsForm(userDetailsForm: UserDetailsForm, patron: User){

    let warningMessages = [];

    if (userDetailsForm.email.value !== patron.email)
        warningMessages.push(`Changing your email address will require you to log in with your new email.`);

    if (userDetailsForm.patronRole.value !== String(patron.patronRole ?? ''))
        warningMessages.push(`Changing your job title will update your approval status to 'Pending,' and you will not be able to log back in until you are approved.`);

    if (userDetailsForm.patronSubType.value !== String(patron.patronSubType ?? ''))
        warningMessages.push(`Changing your role will update your approval status to 'Pending,' and you will not be able to log back in until you are approved.`);

    if (userDetailsForm.organization.value !== patron.organization)
        warningMessages.push(`Changing your organization will update your approval status to 'Pending,' and you will not be able to log back in until you are approved.`);

    if (userDetailsForm.districtId.value !== patron.districtId)
        warningMessages.push(`Changing your district will update your approval status to 'Pending,' and you will not be able to log back in until you are approved.`);

    return warningMessages;
}


export function createPatronRoleId(userDetailsForm: UserDetailsForm) {
    let patronRole = userDetailsForm.jobTitle.value;
    let subtypeKey = userDetailsForm.patronSubType.value;

    patronRole = `${patronRole}, ${subtypeKey}`;

    userDetailsForm.patronRole.setValue(patronRole);

    return userDetailsForm;
}

function checkAndSetError(fields: InputFormField[], message?: string) {
    fields.forEach((field) => {
        if (isTrimmedStringEmpty(field.value) && !message) {
            field.setError(`${field.label} is required.`);
        } else if (isTrimmedStringEmpty(field.value) && message) {
            field.setError(`${message}.`);
        } else if (field.name === "email" && !isValidEmail(field.value)) {
            field.setError("Email is not valid.");
        }
    });
}

export function isLoggedInUserSameAsPatron(patron: User) {
    return patron.userId === getLoggedInUser();
}

export function canUserUpdateApprovalStatus(patron: User) {
    return allow(policyEvents.APPROVE_USERS) && !isLoggedInUserSameAsPatron(patron) && patron.isActive;
}

export function isDistrictSelectionOther(districtId: string, districts: { id: string; districtName: string }[]) {
    const district = districts.find(d => d.id === districtId);
    return district?.districtName === "Other";
}
